import React, { useEffect, useMemo, useRef, useState } from 'react';
import { UploadArea } from '../ui/UploadArea';
import { Input } from '../ui/Input';
import { Select } from '../ui/Select';
import { Badge } from '../ui/Badge';
import { TemplateSlot, EndCardOption, SlotTrimConfig, EffectGroup } from '../../hooks/useTemplates';
import { Asset } from '../../hooks/useAssets';
import { CROP_ANCHOR_OPTIONS, getAcceptedTypes } from '../../lib/slotProps';
import { getVisibleCharCount } from '../../lib/textUtils';
import { CheckCircle2, Circle, ChevronDown, ChevronRight } from 'lucide-react';
import { SlotAsset } from './AssetSlotMapper';
import { groupSlotsByScene } from '../../lib/sceneDefs';
import InlineAssetPreview from './InlineAssetPreview';
import { ENDCARD_OPTIONS } from '../../lib/endcardOptions';
import { getOpeningSegments, getOpeningVideoCount, isOpeningVideoSlot, OPENING_FPS } from '../../lib/openingVideoSlots';
import { getDreamCatEffectPath, storageAssetUrl } from '../../lib/dreamCatEffects';
import { getResolvedEffectSelection } from '../../lib/effectGroups';
import { SlotMediaType, validateAssetTypeForSlot } from '../../lib/assetTypeValidation';

export interface SceneGroupedSlotsProps {
  slots: TemplateSlot[];
  slotAssets: SlotAsset[];
  templateId?: string | null;
  /** Active market code — used to select the correct Dream Cat drop-card overlay. */
  market?: string | null;
  /** When provided, opening video slot (video1–video5) clip lengths are computed
   *  dynamically from this count instead of using static per-slot trim metadata. */
  openingVideoCount?: number;
  isSlotVisible?: (slot: TemplateSlot) => boolean;
  isSlotRequired?: (slot: TemplateSlot) => boolean;
  onAssetAdded: (slotId: string, file: File) => void;
  onAssetRemoved: (slotId: string) => void;
  onTextChanged: (slotId: string, value: string) => void;
  onCropAnchorChanged: (slotId: string, anchor: string) => void;
  onTrimStartChanged: (slotId: string, trimStartSeconds: number) => void;
  onVideoDurationDetected: (slotId: string, durationSeconds: number) => void;
  onGalleryAssetAdded?: (slotId: string, storageUrl: string, name: string, size: number, renderUrl?: string) => void;
  disabled?: boolean;
  selectedSlotId?: string | null;
  onSlotSelected?: (slotId: string) => void;
  /** Predefined end card options loaded from the template's Firestore document. */
  endCardOptions?: EndCardOption[];
  /** Optional metadata-driven effect groups for dropCardVariant slot. */
  effectGroups?: EffectGroup[];
}

// ─── SlotCard ────────────────────────────────────────────────────────────────
// Extracted as a module-level component so it can safely use React hooks for
// the inline asset preview and drag-over state.

interface SlotCardProps {
  slot: TemplateSlot;
  slotAsset?: SlotAsset;
  templateId?: string | null;
  /** Active market code — used to select the correct Dream Cat drop-card overlay. */
  market?: string | null;
  /** Explicit Effect Icon variant override. "auto" or null/undefined defers to market. */
  dropCardVariant?: string | null;
  /** Direct URL override for Effect Icon overlay. */
  dropCardAssetUrl?: string | null;
  /** Number of active opening video slots (1–5). Determines which slot gets the effect icon overlay. */
  openingVideoCount?: number;
  isRequired: boolean;
  isSelected: boolean;
  onSlotSelected?: (slotId: string) => void;
  disabled: boolean;
  /** When set, overrides the trim config derived from static slot metadata. */
  trimConfigOverride?: FixedDurationTrimConfig | null;
  onAssetAdded: (slotId: string, file: File) => void;
  onAssetRemoved: (slotId: string) => void;
  onTextChanged: (slotId: string, value: string) => void;
  onCropAnchorChanged: (slotId: string, anchor: string) => void;
  onTrimStartChanged: (slotId: string, trimStartSeconds: number) => void;
  onVideoDurationDetected: (slotId: string, durationSeconds: number) => void;
  onGalleryAssetAdded?: (slotId: string, storageUrl: string, name: string, size: number, renderUrl?: string) => void;
  endCardOptions?: EndCardOption[];
  effectGroups?: EffectGroup[];
}

const DURATION_EPSILON = 0.0001;

interface FixedDurationTrimConfig {
  durationFrames: number;
  fps: number;
  loopPreview: boolean;
}

function getFixedDurationTrimConfig(trim: SlotTrimConfig | undefined): FixedDurationTrimConfig | null {
  if (!trim || trim.enabled !== true || trim.mode !== 'fixed-duration') return null;
  const durationFrames = trim.duration_frames;
  const fps = trim.fps;
  if (typeof durationFrames !== 'number' || durationFrames <= 0) return null;
  if (typeof fps !== 'number' || fps <= 0) return null;
  return {
    durationFrames,
    fps,
    loopPreview: trim.loop_preview !== false,
  };
}

function formatTimeSeconds(totalSeconds: number): string {
  const safe = Math.max(0, totalSeconds);
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);
  const seconds = safe % 60;
  const secWhole = Math.floor(seconds);
  const millis = Math.round((seconds - secWhole) * 1000);
  return `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(secWhole).padStart(2, '0')}.${String(millis).padStart(3, '0')}`;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

interface FixedDurationTrimSelectorProps {
  templateId?: string | null;
  slotId: string;
  file?: File | null;
  url?: string | null;
  /** Active market code — used to select the correct Dream Cat drop-card overlay. */
  market?: string | null;
  /** Explicit Effect Icon variant override. "auto" or null/undefined defers to market. */
  dropCardVariant?: string | null;
  /** Direct URL override for Effect Icon overlay. */
  dropCardAssetUrl?: string | null;
  /** Number of active opening video slots (1–5). Determines which slot gets the effect icon overlay. */
  openingVideoCount?: number;
  trimConfig: FixedDurationTrimConfig;
  trimStartSeconds: number;
  disabled: boolean;
  onTrimStartChanged: (slotId: string, trimStartSeconds: number) => void;
  onVideoDurationDetected: (slotId: string, durationSeconds: number) => void;
}

const FixedDurationTrimSelector: React.FC<FixedDurationTrimSelectorProps> = ({
  templateId,
  slotId,
  file,
  url: directUrl,
  market,
  dropCardVariant,
  dropCardAssetUrl,
  openingVideoCount,
  trimConfig,
  trimStartSeconds,
  disabled,
  onTrimStartChanged,
  onVideoDurationDetected,
}) => {
  const [objectUrl, setObjectUrl] = useState<string>('');
  const [durationSeconds, setDurationSeconds] = useState<number | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    if (!file) {
      setObjectUrl('');
      setDurationSeconds(null);
      return;
    }
    const created = URL.createObjectURL(file);
    setObjectUrl(created);
    setDurationSeconds(null);
    return () => URL.revokeObjectURL(created);
  }, [file]);

  const resolvedUrl = objectUrl || directUrl || '';
  const requiredDurationSeconds = trimConfig.durationFrames / trimConfig.fps;
  const maxStartSeconds = useMemo(() => {
    if (durationSeconds === null) return 0;
    return Math.max(0, durationSeconds - requiredDurationSeconds);
  }, [durationSeconds, requiredDurationSeconds]);
  const clampedStartSeconds = Math.min(Math.max(0, trimStartSeconds), maxStartSeconds);
  const selectedEndSeconds = clampedStartSeconds + requiredDurationSeconds;
  const frameStepSeconds = 1 / trimConfig.fps;
  const isTooShort =
    durationSeconds !== null &&
    durationSeconds + DURATION_EPSILON < requiredDurationSeconds;
  const showDropCardOverlay =
    templateId === 'ai-dream-cat-v1' &&
    isOpeningVideoSlot(slotId) &&
    slotId === `video${getOpeningVideoCount(openingVideoCount)}`;
  const selectedDropCardUrl = showDropCardOverlay
    ? (dropCardAssetUrl || storageAssetUrl(getDreamCatEffectPath(market, dropCardVariant)))
    : null;
  const atMin = clampedStartSeconds <= DURATION_EPSILON;
  const atMax = clampedStartSeconds >= maxStartSeconds - DURATION_EPSILON;
  const canMove = !disabled && durationSeconds !== null && !isTooShort;

  const moveFrames = (deltaFrames: number) => {
    if (!canMove) return;
    const next = clamp(
      clampedStartSeconds + deltaFrames * frameStepSeconds,
      0,
      maxStartSeconds,
    );
    onTrimStartChanged(slotId, next);
  };

  useEffect(() => {
    if (Math.abs(clampedStartSeconds - trimStartSeconds) <= DURATION_EPSILON) return;
    onTrimStartChanged(slotId, clampedStartSeconds);
  }, [clampedStartSeconds, onTrimStartChanged, slotId, trimStartSeconds]);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (!Number.isFinite(video.duration) || video.duration <= 0) return;
    // Seek to the current start point regardless of loop mode so the preview
    // frame updates when the operator moves the trim slider.
    video.currentTime = clampedStartSeconds;
    if (trimConfig.loopPreview) {
      const maybePlay = video.play();
      if (maybePlay && typeof maybePlay.catch === 'function') {
        maybePlay.catch(() => undefined);
      }
    }
  }, [clampedStartSeconds, trimConfig.loopPreview, resolvedUrl]);

  if (!resolvedUrl) return null;

  return (
    <div className="mt-3 space-y-3 rounded-md border border-border-subtle bg-bg-primary p-3">
      <p className="text-xs text-text-secondary">Choose starting point</p>
      <div className="relative">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <video
          ref={videoRef}
          src={resolvedUrl}
          muted
          playsInline
          preload="metadata"
          autoPlay={trimConfig.loopPreview}
          className="w-full max-h-56 rounded border border-border-subtle bg-black object-contain"
          onLoadedMetadata={(e) => {
            const duration = Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0;
            setDurationSeconds(duration);
            onVideoDurationDetected(slotId, duration);
            if (trimConfig.loopPreview) {
              const maxStart = Math.max(0, duration - requiredDurationSeconds);
              const nextStart = Math.min(Math.max(0, trimStartSeconds), maxStart);
              e.currentTarget.currentTime = nextStart;
              const maybePlay = e.currentTarget.play();
              if (maybePlay && typeof maybePlay.catch === 'function') {
                maybePlay.catch(() => undefined);
              }
            } else {
              e.currentTarget.currentTime = Math.max(0, trimStartSeconds);
            }
          }}
          onTimeUpdate={(e) => {
            if (!trimConfig.loopPreview || isTooShort) return;
            const video = e.currentTarget;
            if (video.currentTime >= selectedEndSeconds || video.currentTime < clampedStartSeconds) {
              video.currentTime = clampedStartSeconds;
              const maybePlay = video.play();
              if (maybePlay && typeof maybePlay.catch === 'function') {
                maybePlay.catch(() => undefined);
              }
            }
          }}
        />
        {selectedDropCardUrl && (
          // eslint-disable-next-line jsx-a11y/media-has-caption
          <video
            key={selectedDropCardUrl}
            src={selectedDropCardUrl}
            autoPlay
            muted
            loop
            playsInline
            className="absolute inset-0 h-full w-full rounded object-cover pointer-events-none"
          />
        )}
      </div>
      <div className="space-y-2">
        <input
          type="range"
          min={0}
          max={maxStartSeconds}
          step={frameStepSeconds}
          value={clampedStartSeconds}
          disabled={disabled || durationSeconds === null || isTooShort}
          onChange={(e) => onTrimStartChanged(slotId, Number(e.currentTarget.value))}
          className="h-3 w-full cursor-pointer accent-accent-red disabled:cursor-not-allowed"
        />
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            className="rounded border border-border-subtle px-2 py-1 text-xs text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canMove || atMin}
            onClick={() => moveFrames(-10)}
          >
            -10f
          </button>
          <button
            type="button"
            className="rounded border border-border-subtle px-2 py-1 text-xs text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canMove || atMin}
            onClick={() => moveFrames(-1)}
          >
            -1f
          </button>
          <button
            type="button"
            className="rounded border border-border-subtle px-2 py-1 text-xs text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canMove || atMax}
            onClick={() => moveFrames(1)}
          >
            +1f
          </button>
          <button
            type="button"
            className="rounded border border-border-subtle px-2 py-1 text-xs text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canMove || atMax}
            onClick={() => moveFrames(10)}
          >
            +10f
          </button>
          <button
            type="button"
            className="rounded border border-border-subtle px-2 py-1 text-xs text-text-primary disabled:opacity-50 disabled:cursor-not-allowed"
            disabled={!canMove || atMin}
            onClick={() => onTrimStartChanged(slotId, 0)}
          >
            Reset to start
          </button>
        </div>
      </div>
      <div className="space-y-1 text-xs">
        <p className="text-text-primary">Start: {formatTimeSeconds(clampedStartSeconds)}</p>
        <p className="text-text-primary">End: {formatTimeSeconds(selectedEndSeconds)}</p>
        <p className="text-text-secondary">
          Clip length fixed by template: {requiredDurationSeconds.toFixed(2)} s
        </p>
      </div>
      {isTooShort && (
        <p className="text-xs font-medium text-accent-red">
          Selected media is too short ({durationSeconds?.toFixed(2)} s). Minimum required is {requiredDurationSeconds.toFixed(2)} s.
        </p>
      )}
    </div>
  );
};




const SlotCard: React.FC<SlotCardProps> = ({
  slot,
  slotAsset,
  templateId,
  market,
  dropCardVariant,
  dropCardAssetUrl,
  openingVideoCount,
  isRequired,
  isSelected,
  onSlotSelected,
  disabled,
  trimConfigOverride,
  onAssetAdded,
  onAssetRemoved,
  onTextChanged,
  onCropAnchorChanged,
  onTrimStartChanged,
  onVideoDurationDetected,
  onGalleryAssetAdded,
  endCardOptions = [],
  effectGroups,
}) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [slotWarning, setSlotWarning] = useState<string | null>(null);

  const isDroppableMediaSlot =
    (slot.type === 'image' || slot.type === 'video' || slot.type === 'audio') &&
    slot.slotId !== 'endCardVideo';
  const isMediaSlot = slot.type === 'image' || slot.type === 'video';
  // Non-media slots that have referencePreview metadata are also selectable so
  // the sidebar can show the template reference clip for that slot.
  const isSelectable = isMediaSlot || !!slot.referencePreview;
  const textValue = slotAsset?.textValue ?? '';
  const maxChars = slot.specs?.max_chars ?? null;
  // trimConfigOverride (if defined) takes priority over static slot trim metadata.
  // undefined means "not overridden"; null means "no trim" (used to suppress static config).
  const trimConfig: FixedDurationTrimConfig | null =
    trimConfigOverride !== undefined
      ? trimConfigOverride
      : slot.type === 'video'
      ? getFixedDurationTrimConfig(slot.trim)
      : null;

  // A slot is pipeline-sourced when the template defines a map_from mapping for it.
  // Pipeline text slots are read-only: the pipeline populates them; operators cannot override.
  const isPipelineSlot = !!slot.map_from;

  // Use Firestore-provided options when available; fall back to the hardcoded list.
  const resolvedEndCardOptions =
    slot.slotId === 'endCardVideo' && (!endCardOptions || endCardOptions.length === 0)
      ? ENDCARD_OPTIONS
      : (endCardOptions ?? []);

  // Standard max_chars limit for text slots
  const visibleCharCount = getVisibleCharCount(textValue, slot.slotId);
  const isOverLimit = maxChars !== null && visibleCharCount > maxChars;

  const isComplete =
    slot.type === 'text' || slot.type === 'select'
      ? !!slotAsset?.textValue && slotAsset.textValue.trim().length > 0
      : slot.type === 'color'
      ? true // color slots are always complete (have defaults)
      : !!slotAsset?.file || !!slotAsset?.storageUrl;

  const selectedEffectSelection = useMemo(
    () => (
      slot.slotId === 'dropCardVariant'
        ? getResolvedEffectSelection(effectGroups, textValue || null)
        : null
    ),
    [effectGroups, slot.slotId, textValue],
  );
  const [selectedEffectGroupId, setSelectedEffectGroupId] = useState<string>(
    selectedEffectSelection?.group.id ?? '',
  );

  useEffect(() => {
    setSelectedEffectGroupId(selectedEffectSelection?.group.id ?? '');
  }, [selectedEffectSelection?.group.id]);

  const selectedEffectGroup = useMemo(
    () =>
      effectGroups?.find((group) => group.id === selectedEffectGroupId)
      ?? selectedEffectSelection?.group
      ?? null,
    [effectGroups, selectedEffectGroupId, selectedEffectSelection?.group],
  );

  const handleDroppedAsset = (dataTransfer: DataTransfer) => {
    if (slot.type === 'text') return;
    try {
      const assetData = dataTransfer.getData('application/json');
      if (assetData) {
        // Internal gallery drag: asset is already in Firebase Storage.
        const asset: Asset = JSON.parse(assetData);
        const validation = validateAssetTypeForSlot(slot.type as SlotMediaType, {
          declaredType: asset.type,
          mimeType: asset.mimeType,
          filename: asset.name,
        });
        if (!validation.valid) {
          setSlotWarning(validation.message ?? 'Invalid asset type for this slot.');
          return;
        }
        setSlotWarning(null);
        // The asset is already in Firebase Storage — pass the URL directly to avoid
        // a cross-origin fetch (which fails in production due to CORS restrictions).
        if (onGalleryAssetAdded) {
          onGalleryAssetAdded(slot.slotId, asset.storageUrl, asset.name, asset.size);
        }
        onSlotSelected?.(slot.slotId);
      } else if (dataTransfer.files.length > 0) {
        // Native OS file drag: upload through the existing slot upload flow.
        const file = dataTransfer.files[0];
        const validation = validateAssetTypeForSlot(slot.type as SlotMediaType, {
          mimeType: file.type,
          filename: file.name,
        });
        if (!validation.valid) {
          setSlotWarning(validation.message ?? 'Invalid asset type for this slot.');
          return;
        }
        setSlotWarning(null);
        onAssetAdded(slot.slotId, file);
        onSlotSelected?.(slot.slotId);
      }
    } catch (error) {
      console.error('Failed to handle dropped asset:', error);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    if (!isDroppableMediaSlot) return;
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    handleDroppedAsset(e.dataTransfer);
  };

  const handleDragOver = (e: React.DragEvent) => {
    if (!isDroppableMediaSlot) return;
    e.preventDefault();
    e.stopPropagation();
    if (!isDragOver) setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    if (!isDroppableMediaSlot) return;
    e.preventDefault();
    e.stopPropagation();
    const nextTarget = e.relatedTarget as Node | null;
    if (!nextTarget || !e.currentTarget.contains(nextTarget)) {
      setIsDragOver(false);
    }
  };

  return (
    <div
      className={`
        p-4 bg-bg-tertiary border rounded-md transition-all duration-150
        ${isSelectable ? 'cursor-pointer' : ''}
        ${isSelectable && isSelected
          ? 'border-accent-red ring-1 ring-accent-red'
          : 'border-border-subtle'}
        ${isDragOver ? 'ring-2 ring-accent-red ring-offset-2' : ''}
      `}
      onDrop={handleDrop}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onClick={
        isSelectable && onSlotSelected
          ? () => onSlotSelected(slot.slotId)
          : undefined
      }
    >
      {/* Header row */}
      <div className="flex items-start justify-between mb-3">
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-1">
            {isComplete ? (
              <CheckCircle2 className="w-4 h-4 text-status-online" />
            ) : (
              <Circle className="w-4 h-4 text-status-offline" />
            )}
            <h4 className="text-sm font-semibold text-text-primary">{slot.label}</h4>
            {isRequired && (
              <Badge size="sm" variant="error">Required</Badge>
            )}
            {isPipelineSlot && (
              <Badge size="sm" variant="default">Pipeline</Badge>
            )}
          </div>
          {slot.description && (
            <p className="text-sm text-text-secondary ml-6 leading-relaxed">{slot.description}</p>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {slot.specs?.aspect_ratio && (
            <Badge size="sm" variant="default">{slot.specs.aspect_ratio}</Badge>
          )}
          <Badge size="sm" variant="default">{slot.type}</Badge>
        </div>
      </div>

      {/* Text slots — editable input (or read-only for pipeline slots) + value display */}
      {slot.type === 'text' && (
        <>
          <Input
            type="text"
            placeholder={isPipelineSlot ? 'Filled by pipeline' : `Enter ${slot.label.toLowerCase()}`}
            value={textValue}
            onChange={(e) => !isPipelineSlot && onTextChanged(slot.slotId, e.target.value)}
            disabled={disabled || isPipelineSlot}
            readOnly={isPipelineSlot}
          />
          {maxChars !== null && (
            <p className={`text-xs mt-1 text-right ${isOverLimit ? 'text-accent-red font-medium' : 'text-text-tertiary'}`}>
              {visibleCharCount} / {maxChars}
              {isOverLimit && ' — exceeds limit'}
            </p>
          )}

          {/* Read-only value display shown once text is entered */}
          <InlineAssetPreview type="text" textValue={textValue || null} />
        </>
      )}

      {/* Select slots — metadata-driven dropdown */}
      {slot.type === 'select' && (
        <>
          {slot.slotId === 'dropCardVariant' && effectGroups && effectGroups.length > 0 ? (
            <div className="space-y-2">
              <Select
                value={selectedEffectGroup?.id ?? ''}
                onChange={(groupId) => {
                  setSelectedEffectGroupId(groupId);
                  const nextGroup = effectGroups.find((group) => group.id === groupId);
                  const nextOption = nextGroup?.options?.[0];
                  if (nextOption) {
                    onTextChanged(slot.slotId, nextOption.id);
                  }
                }}
                options={effectGroups.map((group) => ({ value: group.id, label: group.label }))}
                disabled={disabled}
                className="w-full"
              />
              {selectedEffectGroup?.description && (
                <p className="text-xs text-text-secondary">{selectedEffectGroup.description}</p>
              )}
              <Select
                value={selectedEffectSelection?.option.id ?? ''}
                onChange={(optionId) => onTextChanged(slot.slotId, optionId)}
                options={(selectedEffectGroup?.options ?? []).map((option) => ({
                  value: option.id,
                  label: option.label,
                }))}
                disabled={disabled}
                className="w-full"
              />
              <InlineAssetPreview
                type="text"
                textValue={selectedEffectSelection?.option.label ?? null}
              />
            </div>
          ) : (
            <>
              <Select
                value={textValue || slot.defaultValue || ''}
                onChange={(value) => onTextChanged(slot.slotId, value)}
                options={slot.options ?? []}
                disabled={disabled}
                className="w-full"
              />
              <InlineAssetPreview type="text" textValue={textValue || slot.defaultValue || null} />
            </>
          )}
        </>
      )}

      {/* Color picker slots */}
      {slot.type === 'color' && (
        <div className="flex items-center gap-3">
          <input
            type="color"
            value={textValue || slot.specs?.default || '#000000'}
            onChange={(e) => onTextChanged(slot.slotId, e.target.value)}
            disabled={disabled}
            className="w-16 h-10 rounded cursor-pointer border border-border-primary"
          />
          <span className="text-sm text-text-secondary font-mono">
            {textValue || slot.specs?.default || '#000000'}
          </span>
        </div>
      )}

      {/* File upload for image / video / audio slots */}
      {(slot.type === 'image' || slot.type === 'video' || slot.type === 'audio') && (
        <>
          {/* End Card Video — predefined dropdown, no upload */}
          {slot.slotId === 'endCardVideo' ? (
            <div className="mt-1">
              <Select
                value={slotAsset?.storageUrl
                  ? (resolvedEndCardOptions.find((o) => (o.previewUrl ?? o.videoUrl) === slotAsset.storageUrl)?.id ?? '')
                  : ''}
                onChange={(selectedId) => {
                  const option = resolvedEndCardOptions.find((o) => o.id === selectedId);
                  if (option && onGalleryAssetAdded) {
                    // Size is 0 for predefined assets — the upload area is not
                    // shown for this slot so assetSize is never displayed.
                    // Pass previewUrl as storageUrl (for UI preview) and videoUrl as
                    // renderUrl (for Remotion render input — original MOV).
                    onGalleryAssetAdded(
                      slot.slotId,
                      option.previewUrl ?? option.videoUrl,
                      option.label,
                      0,
                      option.videoUrl,
                    );
                  }
                  // Activate the sidebar slot preview for this slot automatically.
                  onSlotSelected?.(slot.slotId);
                }}
                options={[
                  { value: '', label: 'Select an end card…' },
                  ...resolvedEndCardOptions.map((o) => ({ value: o.id, label: o.label })),
                ]}
                disabled={disabled}
                className="w-full"
              />
            </div>
          ) : (
            <div
              onDrop={handleDrop}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              className={`
                transition-all duration-200 rounded-lg
                ${isDragOver ? 'ring-2 ring-accent-red ring-offset-2' : ''}
              `}
            >
              <UploadArea
                onFilesAdded={(files) => {
                  const file = files[0];
                  if (!file) return;
                  const validation = validateAssetTypeForSlot(slot.type as SlotMediaType, {
                    mimeType: file.type,
                    filename: file.name,
                  });
                  if (!validation.valid) {
                    setSlotWarning(validation.message ?? 'Invalid asset type for this slot.');
                    return;
                  }
                  setSlotWarning(null);
                  onAssetAdded(slot.slotId, file);
                  onSlotSelected?.(slot.slotId);
                }}
                onFilesRejected={(rejections) => {
                  const file = rejections[0]?.file;
                  const validation = validateAssetTypeForSlot(slot.type as SlotMediaType, {
                    mimeType: file?.type,
                    filename: file?.name,
                  });
                  setSlotWarning(validation.message ?? 'Invalid asset type for this slot.');
                }}
                onFileRemove={() => onAssetRemoved(slot.slotId)}
                uploadedFiles={
                  slotAsset?.file
                    ? [{ file: slotAsset.file }]
                    : slotAsset?.storageUrl
                    ? [{ name: slotAsset.assetName ?? 'Gallery asset', size: slotAsset.assetSize }]
                    : []
                }
                accept={getAcceptedTypes(slot.type as 'image' | 'video' | 'audio', slot.specs)}
                maxFiles={1}
                disabled={disabled}
                helperText={`Upload ${slot.type} or drag from gallery (max 100MB)`}
              />
              {slotWarning && (
                <p className="mt-2 text-xs text-status-warning">{slotWarning}</p>
              )}
            </div>
          )}
        </>
      )}

      {/* Inline asset preview when a file is uploaded or a gallery asset is assigned
          (skip endCardVideo — it renders its own inline preview inside the dropdown block) */}
      {(slot.type === 'image' || slot.type === 'video' || slot.type === 'audio') &&
        slot.slotId !== 'endCardVideo' &&
        !(slot.type === 'video' && trimConfig) &&
        (slotAsset?.file || slotAsset?.storageUrl) && (
          <InlineAssetPreview
            type={slot.type}
            file={slotAsset?.file}
            url={slotAsset?.storageUrl}
          />
        )}

      {slot.type === 'video' &&
        trimConfig &&
        slot.slotId !== 'endCardVideo' &&
        (slotAsset?.file || slotAsset?.storageUrl) && (
          <FixedDurationTrimSelector
            templateId={templateId}
            slotId={slot.slotId}
            file={slotAsset?.file}
            url={slotAsset?.storageUrl}
            market={market}
            dropCardVariant={dropCardVariant}
            dropCardAssetUrl={dropCardAssetUrl}
            openingVideoCount={openingVideoCount}
            trimConfig={trimConfig}
            trimStartSeconds={slotAsset?.trimStartSeconds ?? 0}
            disabled={disabled}
            onTrimStartChanged={onTrimStartChanged}
            onVideoDurationDetected={onVideoDurationDetected}
          />
        )}

      {/* Crop anchor selector — shown for cover-fit image/video slots, not for endCardVideo */}
      {(slot.type === 'image' || slot.type === 'video') &&
        slot.slotId !== 'endCardVideo' &&
        slot.specs?.fit_mode !== 'contain' &&
        slot.specs?.show_crop_anchor !== false && (
          <div className="mt-3 flex items-center gap-3">
            <label className="text-xs text-text-secondary whitespace-nowrap">Crop anchor:</label>
            <Select
              value={slotAsset?.cropAnchor ?? slot.specs?.crop_anchor ?? 'center'}
              onChange={(value) => onCropAnchorChanged(slot.slotId, value)}
              options={CROP_ANCHOR_OPTIONS as unknown as { value: string; label: string }[]}
              disabled={disabled}
              className="flex-1 text-xs"
            />
          </div>
        )}
    </div>
  );
};

// ─── SceneGroupedSlots ───────────────────────────────────────────────────────

const isSlotComplete = (slot: TemplateSlot, slotAsset?: SlotAsset): boolean => {
  if (!slotAsset) return slot.type === 'color'; // color slots always complete (have defaults)
  if (slot.type === 'text' || slot.type === 'select') {
    return !!slotAsset.textValue && slotAsset.textValue.trim().length > 0;
  }
  if (slot.type === 'color') {
    return true; // color slots are always complete
  }
  return !!slotAsset.file || !!slotAsset.storageUrl;
};

export const SceneGroupedSlots: React.FC<SceneGroupedSlotsProps> = ({
  slots,
  slotAssets,
  templateId,
  market,
  openingVideoCount,
  isSlotVisible = (slot) => slot.visible !== false,
  isSlotRequired = (slot) => Boolean(slot.required),
  onAssetAdded,
  onAssetRemoved,
  onTextChanged,
  onCropAnchorChanged,
  onTrimStartChanged,
  onVideoDurationDetected,
  onGalleryAssetAdded,
  disabled = false,
  selectedSlotId,
  onSlotSelected,
  endCardOptions,
  effectGroups,
}) => {
  // All scenes collapsed by default per spec.
  const [openScenes, setOpenScenes] = useState<Set<string>>(() => new Set());

  const toggleScene = (sceneId: string) => {
    setOpenScenes((prev) => {
      const next = new Set(prev);
      if (next.has(sceneId)) {
        next.delete(sceneId);
      } else {
        next.add(sceneId);
      }
      return next;
    });
  };

  const getSlotAsset = (slotId: string): SlotAsset | undefined =>
    slotAssets.find((sa) => sa.slotId === slotId);

  // Derive effect selection for the drop-card overlay from grouped metadata when available.
  const selectedEffectSelection = getResolvedEffectSelection(
    effectGroups,
    getSlotAsset('dropCardVariant')?.textValue ?? null,
  );
  const dropCardVariant = selectedEffectSelection?.option.value
    ?? getSlotAsset('dropCardVariant')?.textValue
    ?? null;
  const dropCardAssetUrl = selectedEffectSelection?.option.assetUrl ?? null;

  // Pre-compute opening segment trim configs so each SlotCard gets the correct
  // dynamic clip length rather than the static per-slot trim metadata.
  const openingTrimConfigMap = useMemo((): Map<string, FixedDurationTrimConfig> => {
    if (openingVideoCount === undefined) return new Map();
    return new Map(
      getOpeningSegments(openingVideoCount).map((seg) => [
        seg.slotId,
        { durationFrames: seg.durationFrames, fps: OPENING_FPS, loopPreview: true },
      ]),
    );
  }, [openingVideoCount]);

  // Build scene groups dynamically from slot metadata (scene_id or map_from.scene_id).
  // No hardcoded slot lists — works for any template without code changes.
  const scenes = groupSlotsByScene(slots);

  return (
    <div className="space-y-3">
      {scenes.map((scene) => {
        const isOpen = openScenes.has(scene.id);
        const visibleSlots = scene.slots.filter((slot) => isSlotVisible(slot));
        const filledCount = visibleSlots.filter((slot) =>
          isSlotComplete(slot, getSlotAsset(slot.slotId))
        ).length;
        const totalCount = visibleSlots.length;
        const allFilled = filledCount === totalCount;

        return (
          <div key={scene.id} className="border border-border-subtle rounded-md overflow-hidden">
            <button
              type="button"
              onClick={() => toggleScene(scene.id)}
              className="w-full flex items-center justify-between px-4 py-3 bg-bg-secondary hover:bg-bg-tertiary transition-colors text-left"
            >
              <div className="flex items-center gap-2">
                {isOpen ? (
                  <ChevronDown className="w-4 h-4 text-text-secondary flex-shrink-0" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-text-secondary flex-shrink-0" />
                )}
                <span className="text-sm font-semibold text-text-primary">{scene.label}</span>
              </div>
              <Badge variant={allFilled ? 'success' : 'info'}>
                {filledCount} / {totalCount}
              </Badge>
            </button>

            {isOpen && (
              <div className="p-4 space-y-4 bg-bg-primary">
                {scene.slots
                 .filter((slot) => isSlotVisible(slot))
                 .map((slot) => (
                   <SlotCard
                     key={slot.slotId}
                     slot={slot}
                     slotAsset={getSlotAsset(slot.slotId)}
                     templateId={templateId}
                     market={market}
                     dropCardVariant={dropCardVariant}
                     dropCardAssetUrl={dropCardAssetUrl}
                     openingVideoCount={openingVideoCount}
                     isRequired={isSlotRequired(slot)}
                     isSelected={selectedSlotId === slot.slotId}
                     onSlotSelected={onSlotSelected}
                     disabled={disabled}
                     trimConfigOverride={openingTrimConfigMap.get(slot.slotId)}
                     onAssetAdded={onAssetAdded}
                     onAssetRemoved={onAssetRemoved}
                     onTextChanged={onTextChanged}
                     onCropAnchorChanged={onCropAnchorChanged}
                     onTrimStartChanged={onTrimStartChanged}
                     onVideoDurationDetected={onVideoDurationDetected}
                     onGalleryAssetAdded={onGalleryAssetAdded}
                     endCardOptions={endCardOptions}
                     effectGroups={effectGroups}
                   />
                 ))}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};
